import { LinkData } from 'src/containers/ProjectDiagram/types/model';
import { isModelConceptHierarchy } from 'src/containers/ProjectDiagram/utils/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';

export type ClassHierarchy = {
  ancestors: Record<string, number>;
  descendants: Record<string, number>;
};

export const getClassHierarchy = (
  linkDataArray: LinkData[],
  classIris: Set<string>
): Record<string, ClassHierarchy> => {
  const hierachyByIri: Record<string, ClassHierarchy> = {};
  const childrenByClass: Record<string, Set<string>> = {};
  const parentsByClass: Record<string, Set<string>> = {};

  classIris.forEach((classIri) => {
    hierachyByIri[classIri] = { ancestors: {}, descendants: {} };
    childrenByClass[classIri] = new Set();
    parentsByClass[classIri] = new Set();
  });

  linkDataArray.filter(isModelConceptHierarchy).forEach((linkData) => {
    const { from, to } = linkData;

    const fromIri = getModelConceptClassValue(from);
    const toIri = getModelConceptClassValue(to);

    childrenByClass[toIri]?.add(fromIri);
    parentsByClass[fromIri]?.add(toIri);
  });

  const recordHierarchy =
    (
      baseIri: string,
      key: 'ancestors' | 'descendants',
      classesByIri: Record<string, Set<string>>,
      minDepthByClass: Record<string, number> = {}
    ) =>
    (currentIri: string, depth: number) => {
      const minDepth = minDepthByClass[currentIri];
      // if we've already processed this class at an earlier depth,
      // do not process it again
      if (minDepth && minDepth <= depth) {
        return;
      }

      (classesByIri[currentIri] || []).forEach((nextIri) => {
        if (
          !hierachyByIri[baseIri][key][nextIri] ||
          depth < hierachyByIri[baseIri][key][nextIri]
        ) {
          // eslint-disable-next-line no-param-reassign
          hierachyByIri[baseIri][key][nextIri] = depth;
        }
      });

      // eslint-disable-next-line no-param-reassign
      minDepthByClass[currentIri] = depth;

      classesByIri[currentIri]?.forEach((nextIri) => {
        recordHierarchy(
          baseIri,
          key,
          classesByIri,
          minDepthByClass
        )(nextIri, depth + 1);
      });
    };

  classIris.forEach((classIri) => {
    recordHierarchy(classIri, 'ancestors', parentsByClass)(classIri, 2);
    recordHierarchy(classIri, 'descendants', childrenByClass)(classIri, 2);
  });

  return hierachyByIri;
};
